import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SectionHeader } from '@/components/ui/Primitives';
import { MinorProjectRow, ProjectCard } from '@/components/ui/ProjectCard';
import { Reveal } from '@/components/ui/Reveal';
import { cx } from '@/lib/format';
import type { Bootstrap } from '@/lib/types';
import { useI18n } from '@/store/preferences';

export function ProjectsPage({ data }: { data: Bootstrap }) {
  const { t } = useI18n();
  const [filter, setFilter] = useState<string | null>(null);

  const major = useMemo(() => data.projects.filter((p) => p.scale === 'MAJOR'), [data.projects]);
  const minor = useMemo(() => data.projects.filter((p) => p.scale === 'MINOR'), [data.projects]);

  // Only technologies that actually appear in a major project become chips, so
  // the filter bar can never offer an empty result.
  const filters = useMemo(() => {
    const counts = new Map<string, { slug: string; name: string; count: number }>();
    for (const project of major) {
      for (const tech of project.technologies) {
        const existing = counts.get(tech.slug);
        if (existing) existing.count += 1;
        else counts.set(tech.slug, { slug: tech.slug, name: tech.name, count: 1 });
      }
    }
    return [...counts.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [major]);

  const visible = useMemo(
    () =>
      filter ? major.filter((p) => p.technologies.some((tech) => tech.slug === filter)) : major,
    [major, filter]
  );

  return (
    <div className="pt-32 pb-28 sm:pt-40 sm:pb-36">
      <div className="shell">
        <SectionHeader
          eyebrow={t.sections.selectedWork}
          title={t.sections.allProjects}
          intro={t.sections.selectedWorkTitle}
        />

        <div className="mb-10 flex flex-wrap gap-2" role="group" aria-label={t.project.filterBy}>
          <FilterChip active={filter === null} onClick={() => setFilter(null)}>
            {t.project.filterAll}
            <span className="ml-1.5 text-faint">{major.length}</span>
          </FilterChip>

          {filters.map((entry) => (
            <FilterChip
              key={entry.slug}
              active={filter === entry.slug}
              onClick={() => setFilter(filter === entry.slug ? null : entry.slug)}
            >
              {entry.name}
              <span className="ml-1.5 text-faint">{entry.count}</span>
            </FilterChip>
          ))}
        </div>

        {visible.length === 0 ? (
          <p className="py-20 text-center text-muted">{t.project.noResults}</p>
        ) : (
          // No `popLayout`: it needs flushSync during exit and is a known
          // source of render crashes. A plain fade keeps filtering smooth.
          <div className="grid gap-6 lg:grid-cols-2">
            <AnimatePresence initial={false}>
              {visible.map((project) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  <ProjectCard project={project} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {minor.length > 0 && !filter && (
          <section className="mt-28">
            <div className="rule mb-16" />
            <SectionHeader
              eyebrow={t.sections.minorWork}
              title={t.sections.minorWork}
              intro={t.sections.minorWorkIntro}
            />
            <Reveal>
              <div className="card px-6 sm:px-8">
                {minor.map((project) => (
                  <MinorProjectRow key={project.id} project={project} />
                ))}
              </div>
            </Reveal>
          </section>
        )}
      </div>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cx(
        'rounded-full border px-3.5 py-1.5 font-mono text-2xs tracking-wide transition-all duration-200',
        active
          ? 'border-accent bg-accent/10 text-accent'
          : 'border-line bg-transparent text-muted hover:border-ink/25 hover:text-ink'
      )}
    >
      {children}
    </button>
  );
}
